import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Main from "./Main";
import { makeCompanyNav } from "./nav";
import {
  listWorkspaceTasks,
  createWorkspaceTask,
  updateWorkspaceTask,
  deleteWorkspaceTask,
} from "../services/server";
import "./css/workspace-tools.css";

const PRIORITIES = [
  { value: "low", label: "Låg" },
  { value: "medium", label: "Medel" },
  { value: "high", label: "Hög" },
];

const STATUSES = [
  { value: "todo", label: "Att göra" },
  { value: "in_progress", label: "Pågående" },
  { value: "done", label: "Klar" },
  { value: "blocked", label: "Blockerad" },
];

function fmtDate(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("sv-SE");
}

function isOverdue(task) {
  if (!task.due_date || task.status === "done") return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return new Date(task.due_date) < today;
}

export default function Tasks() {
  const { company } = useParams();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [filter, setFilter] = useState("open");

  const [draftTitle, setDraftTitle] = useState("");
  const [draftPriority, setDraftPriority] = useState("medium");
  const [draftDue, setDraftDue] = useState("");
  const [draftEmail, setDraftEmail] = useState("");
  const [posting, setPosting] = useState(false);

  const load = async () => {
    setLoading(true);
    setErr("");
    try {
      const data = await listWorkspaceTasks(company, filter === "all" ? {} : { status: filter });
      setItems(Array.isArray(data.items) ? data.items : []);
    } catch (e) {
      setErr(e?.message || "Kunde inte ladda tasks");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (company) load(); /* eslint-disable-next-line */ }, [company, filter]);

  const onCreate = async (e) => {
    e.preventDefault();
    const title = draftTitle.trim();
    if (!title || posting) return;
    setPosting(true);
    try {
      const created = await createWorkspaceTask(company, {
        title,
        priority: draftPriority,
        due_date: draftDue || null,
        assignee_email: draftEmail.trim(),
      });
      setItems((prev) => [created, ...prev]);
      setDraftTitle("");
      setDraftEmail("");
      setDraftDue("");
      setDraftPriority("medium");
    } catch (e2) {
      setErr(e2?.message || "Kunde inte skapa task");
    } finally {
      setPosting(false);
    }
  };

  const onToggle = async (task) => {
    const newStatus = task.status === "done" ? "todo" : "done";
    try {
      const updated = await updateWorkspaceTask(company, task.id, { status: newStatus });
      setItems((prev) => prev.map((t) => (t.id === task.id ? updated : t)));
    } catch (e) {
      setErr(e?.message || "Kunde inte uppdatera");
    }
  };

  const onChangeStatus = async (task, status) => {
    try {
      const updated = await updateWorkspaceTask(company, task.id, { status });
      setItems((prev) => prev.map((t) => (t.id === task.id ? updated : t)));
    } catch (e) {
      setErr(e?.message || "Kunde inte uppdatera status");
    }
  };

  const onDelete = async (task) => {
    if (!window.confirm(`Ta bort task "${task.title}"?`)) return;
    try {
      await deleteWorkspaceTask(company, task.id);
      setItems((prev) => prev.filter((t) => t.id !== task.id));
    } catch (e) {
      setErr(e?.message || "Kunde inte ta bort");
    }
  };

  const counts = useMemo(() => {
    const open = items.filter((t) => t.is_open).length;
    const overdue = items.filter((t) => isOverdue(t)).length;
    return { open, overdue, total: items.length };
  }, [items]);

  return (
    <Main navItems={makeCompanyNav(company)}>
      <div className="wt-page">
        <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
          <span className="wt-btn wt-btn--primary">Tasks</span>
          <Link className="wt-btn wt-btn--ghost" to={`/notes/${company}`}>Anteckningar</Link>
        </div>
        <h1 className="wt-page__title">Tasks & anteckningar</h1>
        <p className="wt-page__subtitle">
          {counts.open} öppna · {counts.overdue} försenade · {counts.total} totalt
        </p>

        <section className="wt-card">
          <h3 className="wt-card__title">Ny task</h3>
          <form onSubmit={onCreate}>
            <input
              type="text"
              className="wt-input"
              value={draftTitle}
              onChange={(e) => setDraftTitle(e.target.value)}
              placeholder="t.ex. Be om reviderad balansrapport från CFO"
            />
            <div className="wt-form-row" style={{ marginTop: 8 }}>
              <input
                type="email"
                className="wt-input"
                value={draftEmail}
                onChange={(e) => setDraftEmail(e.target.value)}
                placeholder="Mottagare (e-post)"
              />
              <input
                type="date"
                className="wt-input"
                value={draftDue}
                onChange={(e) => setDraftDue(e.target.value)}
                style={{ flex: "0 0 160px" }}
              />
              <select
                className="wt-select"
                value={draftPriority}
                onChange={(e) => setDraftPriority(e.target.value)}
                style={{ flex: "0 0 120px" }}
              >
                {PRIORITIES.map((p) => (
                  <option key={p.value} value={p.value}>{p.label}</option>
                ))}
              </select>
              <button
                type="submit"
                className="wt-btn wt-btn--primary"
                disabled={posting || !draftTitle.trim()}
              >
                {posting ? "Skapar…" : "Lägg till"}
              </button>
            </div>
          </form>
        </section>

        <section className="wt-card" style={{ marginTop: 16 }}>
          <h3 className="wt-card__title">
            Lista
            <select
              className="wt-select"
              style={{ width: "auto", minWidth: 140 }}
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            >
              <option value="open">Öppna</option>
              <option value="all">Alla</option>
              <option value="todo">Att göra</option>
              <option value="in_progress">Pågående</option>
              <option value="done">Klara</option>
              <option value="blocked">Blockerade</option>
            </select>
          </h3>

          {loading && <p className="wt-empty">Laddar…</p>}
          {!loading && err && <p className="wt-empty">{err}</p>}
          {!loading && !err && items.length === 0 && (
            <p className="wt-empty">Inga tasks i den vyn.</p>
          )}

          {items.map((t) => (
            <TaskRow
              key={t.id}
              task={t}
              onToggle={() => onToggle(t)}
              onChangeStatus={(s) => onChangeStatus(t, s)}
              onDelete={() => onDelete(t)}
            />
          ))}
        </section>
      </div>
    </Main>
  );
}

function TaskRow({ task, onToggle, onChangeStatus, onDelete }) {
  const overdue = isOverdue(task);
  return (
    <div className={`wt-task ${task.status === "done" ? "wt-task--done" : ""}`}>
      <input
        type="checkbox"
        className="wt-task__check"
        checked={task.status === "done"}
        onChange={onToggle}
      />
      <div className="wt-task__body">
        <p className="wt-task__title">{task.title}</p>
        <div className="wt-task__meta">
          <span className={`wt-pill wt-pill--${task.priority}`}>{task.priority}</span>
          <span className={`wt-pill wt-pill--${task.status}`}>{task.status}</span>
          {task.due_date && (
            <span className={overdue ? "wt-pill wt-pill--overdue" : ""}>
              {overdue ? "Försenad: " : "Förfaller: "}{fmtDate(task.due_date)}
            </span>
          )}
          {(task.assignee?.name || task.assignee_email) && (
            <span>→ {task.assignee?.name || task.assignee_email}</span>
          )}
        </div>
      </div>
      <div className="wt-row-actions">
        <select
          className="wt-select"
          style={{ width: "auto", minWidth: 110 }}
          value={task.status}
          onChange={(e) => onChangeStatus(e.target.value)}
        >
          {STATUSES.map((s) => (
            <option key={s.value} value={s.value}>{s.label}</option>
          ))}
        </select>
        <button type="button" className="wt-btn wt-btn--ghost wt-btn--danger" onClick={onDelete}>
          Ta bort
        </button>
      </div>
    </div>
  );
}
